import { useState, useEffect } from 'react';
import { trpc } from '../../lib/trpc.js';
import { getStoredUser } from '../../lib/auth.js';

const tabs = ['Gallery', 'Generate', 'Ad Swipe File', 'Templates'];

interface StudioAsset {
  id: string;
  name?: string;
  type?: string;
  model?: string;
  status?: string;
  format?: string;
  campaign?: string;
  createdAt?: string | Date;
}

const models = [
  { name: 'Veo 3.1', type: 'video', quality: 'Ultra' },
  { name: 'Kling 3.0', type: 'video', quality: 'High' },
  { name: 'Seedance 2.0', type: 'video', quality: 'High' },
  { name: 'FLUX 2', type: 'image', quality: 'Ultra' },
  { name: 'GPT Image 2', type: 'image', quality: 'High' },
  { name: 'Nano Banana Pro', type: 'image', quality: 'Fast' },
];

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    complete: 'bg-emerald-500/20 text-emerald-400',
    processing: 'bg-blue-500/20 text-blue-400',
    failed: 'bg-rose-500/20 text-rose-400',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-slate-700 text-slate-300'}`}>{status}</span>;
}

function formatRelative(date: string | Date | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function GallerySkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="aspect-square bg-slate-800" />
          <div className="p-4 space-y-2">
            <div className="h-4 bg-slate-800 rounded w-3/4" />
            <div className="h-3 bg-slate-800 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function StudioPage() {
  const [activeTab, setActiveTab] = useState('Gallery');
  const [selectedModel, setSelectedModel] = useState('FLUX 2');
  const [prompt, setPrompt] = useState('');
  const [assets, setAssets] = useState<StudioAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.studio.list.query({ tenantId: user.tenantId })
      .then(res => setAssets((res.items ?? []) as StudioAsset[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">🎬 Studio</h1>
        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">
          + Generate
        </button>
      </div>

      <div className="overflow-x-auto -mx-1">
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800 min-w-max mx-1">
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
          >{tab}</button>
        ))}
        </div>
      </div>

      {activeTab === 'Gallery' && (
        loading ? <GallerySkeleton /> : (
          assets.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
              <div className="text-5xl mb-4 opacity-50">🎬</div>
              <h3 className="text-lg font-semibold text-white mb-2">No assets yet</h3>
              <p className="text-slate-400 mb-4">Generate your first creative to populate the gallery.</p>
              <button onClick={() => setActiveTab('Generate')} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm">
                + Generate Creative
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map(asset => (
                <div key={asset.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-700 cursor-pointer group">
                  <div className="aspect-square bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                    <span className="text-5xl opacity-30 group-hover:opacity-50 transition-opacity">
                      {asset.type === 'video' ? '🎬' : '🖼️'}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-white truncate">{asset.name ?? 'Untitled'}</h3>
                      {asset.status && <StatusBadge status={asset.status} />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      {asset.model && <span>{asset.model}</span>}
                      {asset.format && <><span>•</span><span>{asset.format}</span></>}
                      {asset.createdAt && <><span>•</span><span>{formatRelative(asset.createdAt)}</span></>}
                    </div>
                    {asset.campaign && <div className="text-xs text-slate-500 mt-1">{asset.campaign}</div>}
                  </div>
                </div>
              ))}
            </div>
          )
        )
      )}

      {activeTab === 'Generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Generate Creative</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Model</label>
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white">
                  {models.map(m => (
                    <option key={m.name} value={m.name}>{m.name} ({m.type}, {m.quality})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Prompt</label>
                <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                  placeholder="A professional auto insurance ad showing a family happily driving..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 min-h-[120px] resize-none" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Format</label>
                <div className="flex gap-2">
                  {['1080x1080', '1080x1920', '1920x1080', '1200x628'].map(f => (
                    <button key={f} className="px-3 py-1 bg-slate-800 border border-slate-700 rounded text-xs text-slate-300 hover:bg-slate-700">
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <button className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">
                Generate
              </button>
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="text-5xl mb-4 opacity-30">✨</div>
              <p className="text-slate-500 text-sm">Preview will appear here</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Ad Swipe File' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">🔍</div>
          <h3 className="text-lg font-semibold text-white mb-2">Competitor Ad Discovery</h3>
          <p className="text-slate-400 mb-4">Search 100M+ ads from Meta, TikTok, YouTube via Foreplay integration</p>
          <div className="flex gap-3 justify-center">
            <input type="text" placeholder="Search ads or enter domain..." className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white placeholder-slate-500 w-80" />
            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm">Search</button>
          </div>
        </div>
      )}

      {activeTab === 'Templates' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {['Auto Insurance', 'Medicare', 'Home Insurance', 'Legal', 'Health Insurance', 'Life Insurance', 'Debt Relief', 'Solar'].map(template => (
            <div key={template} className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center hover:border-slate-700 cursor-pointer group">
              <div className="text-3xl mb-3 opacity-50 group-hover:opacity-80 transition-opacity">📋</div>
              <h3 className="text-sm font-medium text-white">{template}</h3>
              <p className="text-xs text-slate-400 mt-1">Pre-built creative templates</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
